import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScanLine, Camera, Upload, Clock, CheckCircle, Archive, Send } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function WorkerScanner() {
  const [scans, setScans] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [file, setFile] = useState(null);
  const [formData, setFormData] = useState({ title: '', description: '', scan_type: 'ddt', cantiere_id: '' });

  const fetchData = useCallback(async () => {
    try {
      const [scansRes, statsRes] = await Promise.all([
        axios.get(`${API}/worker-scans`, { withCredentials: true }),
        axios.get(`${API}/dashboard/stats`, { withCredentials: true })
      ]);
      setScans(scansRes.data);
      setCantieri(statsRes.data.assigned_cantieri || []);
    } catch (error) { console.error('Errore caricamento scansioni:', error); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error('Seleziona o scatta una foto del documento'); return; }
    setUploading(true);
    try {
      const uploadForm = new FormData();
      uploadForm.append('file', file);
      const uploadRes = await axios.post(`${API}/upload`, uploadForm, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      await axios.post(`${API}/worker-scans`, { ...formData, file_url: uploadRes.data.path, file_type: file.type }, { withCredentials: true });
      toast.success('Documento inviato all\'admin');
      setShowForm(false);
      setFile(null);
      setFormData({ title: '', description: '', scan_type: 'ddt', cantiere_id: '' });
      fetchData();
    } catch (error) { toast.error('Errore nell\'invio'); }
    finally { setUploading(false); }
  };

  const statusConfig = {
    inviato: { label: 'Inviato', icon: Clock, color: 'bg-amber-100 text-amber-800 border-amber-200' },
    visionato: { label: 'Visionato', icon: CheckCircle, color: 'bg-blue-100 text-blue-800 border-blue-200' },
    archiviato: { label: 'Archiviato', icon: Archive, color: 'bg-emerald-100 text-emerald-800 border-emerald-200' }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;

  if (showForm) {
    return (
      <div className="space-y-4" data-testid="worker-scanner-form">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-slate-900">Invia Documento</h1>
          <Button variant="ghost" onClick={() => setShowForm(false)}>Annulla</Button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Card className="border-slate-200">
            <CardContent className="p-4 space-y-4">
              {/* Camera / File upload */}
              <div className="space-y-2">
                <Label>Foto / File del documento</Label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex flex-col items-center gap-2 p-6 border-2 border-dashed border-blue-300 rounded-lg cursor-pointer bg-blue-50 hover:bg-blue-100 transition-colors">
                    <Camera className="h-8 w-8 text-blue-600" />
                    <span className="text-xs font-medium text-blue-700">Scatta Foto</span>
                    <input type="file" accept="image/*" capture="environment" onChange={(e) => setFile(e.target.files[0])} className="hidden" data-testid="camera-input" />
                  </label>
                  <label className="flex flex-col items-center gap-2 p-6 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
                    <Upload className="h-8 w-8 text-slate-500" />
                    <span className="text-xs font-medium text-slate-600">Scegli File</span>
                    <input type="file" accept="image/*,.pdf,.doc,.docx" onChange={(e) => setFile(e.target.files[0])} className="hidden" data-testid="file-input" />
                  </label>
                </div>
                {file && (
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-emerald-600" />
                    <span className="text-sm text-emerald-800 truncate">{file.name}</span>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Titolo</Label>
                <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required placeholder="es. DDT Fornitore XYZ" data-testid="scan-title-input" />
              </div>
              <div className="space-y-2">
                <Label>Tipo Documento</Label>
                <Select value={formData.scan_type} onValueChange={(v) => setFormData({...formData, scan_type: v})}>
                  <SelectTrigger data-testid="scan-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ddt">DDT</SelectItem>
                    <SelectItem value="scontrino">Scontrino</SelectItem>
                    <SelectItem value="fattura">Fattura</SelectItem>
                    <SelectItem value="altro">Altro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {cantieri.length > 0 && (
                <div className="space-y-2">
                  <Label>Cantiere (opzionale)</Label>
                  <Select value={formData.cantiere_id} onValueChange={(v) => setFormData({...formData, cantiere_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Seleziona cantiere" /></SelectTrigger>
                    <SelectContent>{cantieri.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label>Note (opzionale)</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder="Aggiungi dettagli..." data-testid="scan-description-input" />
              </div>
            </CardContent>
          </Card>
          <Button type="submit" className="w-full h-12 bg-slate-900 text-white hover:bg-slate-800 text-base" disabled={uploading} data-testid="send-scan-btn">
            <Send className="h-5 w-5 mr-2" />{uploading ? 'Invio in corso...' : 'Invia all\'Admin'}
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="worker-scanner">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-slate-900">Scanner Documenti</h1>
      </div>

      {/* CTA */}
      <button onClick={() => setShowForm(true)} className="w-full p-6 bg-slate-900 text-white rounded-xl flex flex-col items-center gap-3 active:scale-95 transition-transform" data-testid="start-scan-btn">
        <ScanLine className="h-10 w-10" />
        <span className="font-medium">Scannerizza / Invia Documento</span>
        <span className="text-xs text-slate-300">DDT, scontrini, fatture</span>
      </button>

      {/* History */}
      <div>
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">I Tuoi Documenti Inviati</h2>
        {scans.length === 0 ? (
          <Card className="border-slate-200"><CardContent className="py-8 text-center text-slate-500"><ScanLine className="h-10 w-10 mx-auto text-slate-300 mb-2" />Nessun documento inviato</CardContent></Card>
        ) : (
          <div className="space-y-2">
            {scans.map((scan) => {
              const status = statusConfig[scan.status] || statusConfig.inviato;
              const StatusIcon = status.icon;
              return (
                <Card key={scan.id} className="border-slate-200">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="p-2 bg-slate-100 rounded-lg flex-shrink-0"><ScanLine className="h-4 w-4 text-slate-600" /></div>
                        <div className="min-w-0">
                          <p className="font-medium text-slate-900 text-sm truncate">{scan.title}</p>
                          <p className="text-xs text-slate-400">{format(new Date(scan.created_at), 'dd MMM yyyy, HH:mm', { locale: it })}</p>
                        </div>
                      </div>
                      <Badge variant="outline" className={`${status.color} text-xs flex-shrink-0`}>
                        <StatusIcon className="h-3 w-3 mr-1" />{status.label}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
